import React, { useState } from 'react'
import {
  Box,
  Card,
  Chip,
  Collapse,
  LinearProgress,
  MenuItem,
  Stack,
  Tabs,
  Tab,
  TextField,
  Button,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import FlagIcon from '@mui/icons-material/Flag'
// LAP-765-v2 — icônes pour le CTA "Programmer" et le panneau emploi.
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'
// LAP-765-v3 — icônes du bloc "Ajout rapide".
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import WTypography from '../../../Components/whiteTypography'
import { TIME_HORIZONS, getTimeCategoryOptions } from '../utils/buildTimeAdvisor'

/**
 * LAP-765 — Sections visuelles du Conseiller Temps.
 * Regroupées dans un fichier unique pour limiter la dispersion (le module ne
 * compte que 4 ou 5 sections, ça ne justifie pas un fichier par section).
 *
 *   - TimeScoreCard       : score global 0-100 + décomposition par dimension
 *   - TimeDiagnosticPanel : forces / faiblesses / risques
 *   - TimeActionsPanel    : liste d'actions prioritaires
 *   - TimeBlocksPanel     : suggestions de time-blocks par horizon
 *                           (semaine / mois / trimestre / année)
 */

const SCORE_COLORS = {
  good: '#4ade80',
  warning: '#fbbf24',
  critical: '#f87171',
}

// LAP-765-v4 — gris neutre pour les dimensions « Non évalué » (aucune donnée).
const NEUTRAL_COLOR = '#94a3b8'

function scoreColor(score) {
  // LAP-765-v4 — `null`/`undefined` = score non calculable (aucune donnée).
  if (score == null) return NEUTRAL_COLOR
  if (score >= 70) return SCORE_COLORS.good
  if (score >= 45) return SCORE_COLORS.warning
  return SCORE_COLORS.critical
}

// ─────────────────────────────────────────────────────────────────────────
// Score
// ─────────────────────────────────────────────────────────────────────────

export function TimeScoreCard({ score, userFirstName, employment, metrics }) {
  const { t } = useTranslation('layout')
  const color = scoreColor(score.global)

  return (
    <Card
      sx={{
        background: 'linear-gradient(135deg, #0f1d3a 0%, #1a2f5e 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: { xs: 2.5, md: 4 },
        color: 'white',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <WTypography variant="overline" sx={{ color: '#a0b4d6 !important', letterSpacing: 1.5 }}>
            {t('conseillerTemps.score.overline', { defaultValue: 'Diagnostic temps' })}
          </WTypography>
          <WTypography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
            {t('conseillerTemps.score.title', {
              defaultValue: `Comment vous gérez votre temps, ${userFirstName || ''}`,
              name: userFirstName,
            })}
          </WTypography>
          <WTypography variant="body2" sx={{ color: '#c1d0eb !important', mt: 1 }}>
            {t('conseillerTemps.score.subtitle', {
              defaultValue:
                'Une note synthétique calculée sur votre calendrier, vos tâches, vos objectifs et votre équilibre de vie.',
            })}
          </WTypography>
          {/* LAP-765-v4 — Couverture du diagnostic : on indique clairement
              combien de dimensions ont réellement des données. Sans ça, un
              profil quasi vide affichait un score « complet » trompeur. */}
          {typeof score.evaluatedCount === 'number' &&
            score.evaluatedCount < (score.totalCount || 6) && (
              <WTypography
                variant="caption"
                sx={{ color: '#a0b4d6 !important', mt: 1, display: 'block' }}
              >
                {t('conseillerTemps.score.coverage', {
                  defaultValue: `Score calculé sur ${score.evaluatedCount}/${
                    score.totalCount || 6
                  } dimensions — les autres sont « Non évalué » faute de données. Complétez votre profil pour les activer.`,
                  evaluated: score.evaluatedCount,
                  total: score.totalCount || 6,
                })}
              </WTypography>
            )}
          {/* LAP-765-v2 — Rappel emploi + heures occupées par les récurrences,
              juste sous le titre, pour ancrer le diagnostic dans la réalité
              concrète de l'utilisateur. */}
          <Stack direction="row" gap={1} flexWrap="wrap" mt={1.5}>
            {employment?.jobTitle && (
              <Chip
                icon={<WorkOutlineIcon sx={{ color: '#a8c1f0 !important' }} />}
                label={
                  employment.employer
                    ? `${employment.jobTitle} · ${employment.employer}`
                    : employment.jobTitle
                }
                sx={{
                  bgcolor: 'rgba(126,156,219,0.14)',
                  color: '#a8c1f0',
                  border: '1px solid rgba(126,156,219,0.3)',
                  fontWeight: 700,
                }}
              />
            )}
            {!!employment?.weeklyWorkHours && (
              <Chip
                size="small"
                label={t('conseillerTemps.score.workHoursChip', {
                  defaultValue: `${employment.weeklyWorkHours} h / semaine`,
                  hours: employment.weeklyWorkHours,
                })}
                sx={{
                  bgcolor: 'rgba(169, 135, 255, 0.15)',
                  color: '#cdbcff',
                  fontWeight: 700,
                }}
              />
            )}
            {!!metrics?.weeklyEventHours && (
              <Chip
                size="small"
                icon={<AccessTimeIcon sx={{ color: '#7cc6b2 !important' }} />}
                label={t('conseillerTemps.score.recurringHoursChip', {
                  defaultValue: `${metrics.weeklyEventHours} h récurrentes / sem.`,
                  hours: metrics.weeklyEventHours,
                })}
                sx={{
                  bgcolor: 'rgba(124, 198, 178, 0.15)',
                  color: '#9ee0cf',
                  fontWeight: 700,
                }}
              />
            )}
          </Stack>
        </Box>
        <ScoreCircle value={score.global} color={color} t={t} />
      </Box>

      <WTypography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#dbe5f7 !important' }}>
        {t('conseillerTemps.score.dimensionsTitle', { defaultValue: 'Détail par dimension' })}
      </WTypography>
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }}
        gap={2}
      >
        {score.dimensions.map((d) => (
          <DimensionRow key={d.key} dim={d} t={t} />
        ))}
      </Box>
    </Card>
  )
}

function ScoreCircle({ value, color, t }) {
  const size = 150
  const stroke = 10
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  // LAP-765-v4 — `value` peut être `null` si aucune dimension n'est évaluable.
  const hasValue = typeof value === 'number' && Number.isFinite(value)
  const offset = circumference * (1 - (hasValue ? value : 0) / 100)

  return (
    <Box position="relative" width={size} height={size}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1.2s ease-out' }}
        />
      </svg>
      <Box
        position="absolute"
        top={0}
        left={0}
        width={size}
        height={size}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <WTypography sx={{ fontSize: 44, fontWeight: 800, lineHeight: 1 }}>
          {hasValue ? value : '—'}
        </WTypography>
        <WTypography sx={{ fontSize: 13, color: '#a0b4d6 !important' }}>
          {hasValue
            ? t('conseillerTemps.score.outOf100', { defaultValue: '/ 100' })
            : t('conseillerTemps.score.noData', { defaultValue: 'non évalué' })}
        </WTypography>
      </Box>
    </Box>
  )
}

function DimensionRow({ dim, t }) {
  // LAP-765-v4 — Une dimension sans données (`available === false`) n'affiche
  // plus un chiffre trompeur : elle passe en « Non évalué », barre grisée.
  const available = dim.available !== false
  const color = available ? scoreColor(dim.score) : NEUTRAL_COLOR
  const statusColor = available
    ? SCORE_COLORS[dim.status] || NEUTRAL_COLOR
    : NEUTRAL_COLOR
  const notEvaluatedLabel = t('conseillerTemps.score.notEvaluated', {
    defaultValue: 'Non évalué',
  })
  // LAP-765-v4 — Le tooltip explicite désormais les données utilisées.
  const tooltipText = available
    ? [dim.detail, dim.dataSource].filter(Boolean).join(' ')
    : dim.emptyHint || dim.detail

  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.06)',
          cursor: 'help',
          transition: 'all .2s',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.07)', transform: 'translateY(-2px)' },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <WTypography variant="body2" sx={{ fontWeight: 600 }}>
            {dim.label}
          </WTypography>
          <WTypography
            variant="body2"
            sx={{
              color: `${color} !important`,
              fontWeight: 700,
              fontSize: available ? undefined : 12,
            }}
          >
            {available ? dim.score : notEvaluatedLabel}
          </WTypography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={available ? dim.score : 0}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.08)',
            '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
          }}
        />
        <Box display="flex" alignItems="center" gap={0.5} mt={1}>
          <Box width={6} height={6} borderRadius="50%" bgcolor={statusColor} />
          <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
            {t('conseillerTemps.score.weightLabel', {
              defaultValue: `pondération ${Math.round(dim.weight * 100)} %`,
              weight: Math.round(dim.weight * 100),
            })}
          </WTypography>
        </Box>
        {/* LAP-765-v4 — Explication, visible dans la page, des données qui
            alimentent la dimension (ou de ce qu'il faut renseigner si vide). */}
        {(dim.dataSource || dim.emptyHint) && (
          <WTypography
            variant="caption"
            sx={{
              color: '#8294b5 !important',
              display: 'block',
              mt: 0.75,
              lineHeight: 1.4,
            }}
          >
            {available ? dim.dataSource : dim.emptyHint}
          </WTypography>
        )}
      </Box>
    </Tooltip>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Diagnostic
// ─────────────────────────────────────────────────────────────────────────

export function TimeDiagnosticPanel({ diagnostic, recommendations = [] }) {
  const { t } = useTranslation('layout')
  return (
    <Card
      sx={{
        bgcolor: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: { xs: 2, md: 3 },
        color: 'white',
      }}
    >
      <WTypography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        {t('conseillerTemps.diagnostic.title', { defaultValue: 'Forces, faiblesses, risques' })}
      </WTypography>

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr 1fr' }} gap={2}>
        <DiagColumn
          icon={<CheckCircleOutlineIcon sx={{ color: SCORE_COLORS.good }} />}
          color={SCORE_COLORS.good}
          title={t('conseillerTemps.diagnostic.strengths', { defaultValue: 'Forces' })}
          items={diagnostic.strengths}
          empty={t('conseillerTemps.diagnostic.strengthsEmpty', {
            defaultValue: 'Pas encore de point fort identifié — c’est normal, ça vient avec la pratique.',
          })}
        />
        <DiagColumn
          icon={<WarningAmberIcon sx={{ color: SCORE_COLORS.warning }} />}
          color={SCORE_COLORS.warning}
          title={t('conseillerTemps.diagnostic.weaknesses', { defaultValue: 'Faiblesses' })}
          items={diagnostic.weaknesses}
          empty={t('conseillerTemps.diagnostic.weaknessesEmpty', {
            defaultValue: 'Aucune zone moyenne, c’est rare et excellent.',
          })}
        />
        <DiagColumn
          icon={<ReportProblemOutlinedIcon sx={{ color: SCORE_COLORS.critical }} />}
          color={SCORE_COLORS.critical}
          title={t('conseillerTemps.diagnostic.risks', { defaultValue: 'Risques' })}
          items={diagnostic.risks}
          empty={t('conseillerTemps.diagnostic.risksEmpty', {
            defaultValue: 'Pas de risque critique pour le moment. Continuez à monitorer.',
          })}
        />
      </Box>

      {/* LAP-765-v4 — Dimensions non évaluées : on explique pourquoi certaines
          dimensions n'apparaissent ni en force, ni en faiblesse, ni en risque
          (données manquantes), au lieu de les laisser fausser le diagnostic. */}
      {diagnostic.notEvaluated && diagnostic.notEvaluated.length > 0 && (
        <Box
          mt={2.5}
          sx={{
            p: 1.75,
            borderRadius: 2,
            bgcolor: 'rgba(148,163,184,0.08)',
            border: '1px solid rgba(148,163,184,0.25)',
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <HelpOutlineIcon sx={{ color: NEUTRAL_COLOR, fontSize: 18 }} />
            <WTypography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: `${NEUTRAL_COLOR} !important` }}
            >
              {t('conseillerTemps.diagnostic.notEvaluated', {
                defaultValue: 'Dimensions non évaluées',
              })}
            </WTypography>
          </Box>
          <WTypography
            variant="caption"
            sx={{ color: '#a0b4d6 !important', display: 'block', mb: 1 }}
          >
            {t('conseillerTemps.diagnostic.notEvaluatedHint', {
              defaultValue:
                'Faute de données, ces dimensions ne comptent pas dans le score et n’apparaissent ni en force ni en risque. Renseignez-les pour les activer.',
            })}
          </WTypography>
          <Stack gap={0.75}>
            {diagnostic.notEvaluated.map((it) => (
              <Box key={it.key}>
                <WTypography variant="body2" sx={{ fontWeight: 600 }}>
                  {it.title}
                </WTypography>
                <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
                  {it.detail}
                </WTypography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {recommendations.length > 0 && (
        <Box mt={3}>
          <WTypography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t('conseillerTemps.diagnostic.recoTitle', {
              defaultValue: 'Recommandations personnalisées',
            })}
          </WTypography>
          <Stack gap={1}>
            {recommendations.map((r, i) => (
              <Box
                key={i}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'rgba(126,156,219,0.10)',
                  border: '1px solid rgba(126,156,219,0.25)',
                }}
              >
                <WTypography variant="body2" sx={{ color: '#dbe5f7 !important' }}>
                  {r}
                </WTypography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Card>
  )
}

function DiagColumn({ icon, color, title, items, empty }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.03)',
        border: `1px solid ${color}33`,
        minHeight: 140,
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1.25}>
        {icon}
        <WTypography variant="subtitle2" sx={{ fontWeight: 700, color: `${color} !important` }}>
          {title}
        </WTypography>
      </Box>
      {items.length === 0 ? (
        <WTypography variant="body2" sx={{ color: '#a0b4d6 !important', fontStyle: 'italic' }}>
          {empty}
        </WTypography>
      ) : (
        <Stack gap={0.75}>
          {items.map((it) => (
            <Box key={it.key}>
              <WTypography variant="body2" sx={{ fontWeight: 600 }}>
                {it.title}
              </WTypography>
              <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
                {it.detail}
              </WTypography>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Priority actions
// ─────────────────────────────────────────────────────────────────────────

const IMPACT_COLORS = {
  high: '#f87171',
  medium: '#fbbf24',
  low: '#4ade80',
}

export function TimeActionsPanel({ actions }) {
  const { t } = useTranslation('layout')
  const navigate = useNavigate()
  if (!actions || actions.length === 0) return null

  return (
    <Card
      sx={{
        bgcolor: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: { xs: 2, md: 3 },
        color: 'white',
      }}
    >
      <WTypography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        {t('conseillerTemps.actions.title', { defaultValue: 'Actions prioritaires' })}
      </WTypography>
      <Stack gap={1.5}>
        {actions.map((a) => (
          <Box
            key={a.id}
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <FlagIcon sx={{ color: IMPACT_COLORS[a.impact] || '#94a3b8', fontSize: 18 }} />
                <WTypography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {a.title}
                </WTypography>
                <Chip
                  size="small"
                  label={t(`conseillerTemps.actions.impact.${a.impact}`, {
                    defaultValue: a.impact,
                  })}
                  sx={{
                    bgcolor: `${IMPACT_COLORS[a.impact] || '#94a3b8'}22`,
                    color: IMPACT_COLORS[a.impact] || '#94a3b8',
                    fontWeight: 700,
                    height: 22,
                  }}
                />
                {a.effortMinutes && (
                  <Chip
                    size="small"
                    icon={<AccessTimeIcon sx={{ color: '#a0b4d6 !important' }} />}
                    label={t('conseillerTemps.actions.effortMinutes', {
                      defaultValue: `${a.effortMinutes} min`,
                      minutes: a.effortMinutes,
                    })}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.05)',
                      color: '#dbe5f7',
                      height: 22,
                    }}
                  />
                )}
              </Box>
              <WTypography variant="body2" sx={{ color: '#c1d0eb !important' }}>
                {a.description}
              </WTypography>
            </Box>
            {a.route && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(a.route)}
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#a8c1f0',
                  borderColor: 'rgba(126,156,219,0.45)',
                  '&:hover': { bgcolor: 'rgba(126,156,219,0.12)' },
                }}
              >
                {t('conseillerTemps.actions.cta', { defaultValue: 'Y aller' })}
              </Button>
            )}
          </Box>
        ))}
      </Stack>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Time-blocks by horizon
// ─────────────────────────────────────────────────────────────────────────

const HORIZON_LABEL_KEY = {
  week: 'conseillerTemps.horizons.week',
  month: 'conseillerTemps.horizons.month',
  quarter: 'conseillerTemps.horizons.quarter',
  year: 'conseillerTemps.horizons.year',
}

const HORIZON_DEFAULT_LABEL = {
  week: 'Semaine',
  month: 'Mois',
  quarter: 'Trimestre',
  year: 'Année',
}

// ─────────────────────────────────────────────────────────────────────────
// LAP-765-v2 — Répartition du temps par catégorie
//
// Affiche une barre horizontale par catégorie (Travail, Personnel, Social,
// Sport, …) sur la fenêtre 30 jours, en s'appuyant sur les occurrences
// dépliées (donc les échéanciers sont bien comptés).
// ─────────────────────────────────────────────────────────────────────────
export function TimeBreakdownPanel({ breakdown }) {
  const { t } = useTranslation('layout')
  const entries = breakdown?.entries || []
  if (entries.length === 0) return null

  const maxMinutes = entries.reduce((m, e) => Math.max(m, e.minutes), 0) || 1

  return (
    <Card
      sx={{
        bgcolor: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: { xs: 2, md: 3 },
        color: 'white',
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={1}
      >
        <WTypography variant="h6" sx={{ fontWeight: 700 }}>
          {t('conseillerTemps.breakdown.title', {
            defaultValue: 'Répartition de votre temps (30 jours)',
          })}
        </WTypography>
        <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
          {t('conseillerTemps.breakdown.hint', {
            defaultValue:
              'Inclut les échéanciers : chaque récurrence compte autant que ses occurrences.',
          })}
        </WTypography>
      </Box>

      <Stack gap={1.5}>
        {entries.map((row) => (
          <Box key={row.category + (row.synthetic ? '-syn' : '')}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: row.color,
                  }}
                />
                <WTypography variant="body2" sx={{ fontWeight: 600 }}>
                  {row.category}
                  {row.synthetic && (
                    <Box
                      component="span"
                      sx={{
                        ml: 1,
                        px: 0.6,
                        py: 0.1,
                        bgcolor: 'rgba(255,255,255,0.06)',
                        borderRadius: 1,
                        fontSize: 10,
                        color: '#a0b4d6',
                      }}
                    >
                      {t('conseillerTemps.breakdown.estimated', {
                        defaultValue: 'estimé',
                      })}
                    </Box>
                  )}
                </WTypography>
              </Box>
              <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
                {t('conseillerTemps.breakdown.row', {
                  defaultValue: `${row.hours} h · ${row.percent} %`,
                  hours: row.hours,
                  percent: row.percent,
                })}
              </WTypography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(row.minutes / maxMinutes) * 100}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'rgba(255,255,255,0.06)',
                '& .MuiLinearProgress-bar': { bgcolor: row.color, borderRadius: 4 },
              }}
            />
          </Box>
        ))}
      </Stack>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// LAP-765-v3 — Ajout rapide d'un créneau dans l'agenda
//
// Mini-formulaire inline en haut du panneau Time-blocks : titre + catégorie
// + récurrence (ponctuel / hebdo N×/sem via chips jours / mensuel / quotidien)
// + heure + durée. La création réelle est déléguée au parent via `onQuickAdd`,
// qui poste sur /api/time/events/ puis rafraîchit la synthèse temps.
// ─────────────────────────────────────────────────────────────────────────

const QUICKADD_WEEKDAYS = [
  { flag: 'monday', key: 'conseillerTemps.quickAdd.mon', defaultValue: 'Lun' },
  { flag: 'tuesday', key: 'conseillerTemps.quickAdd.tue', defaultValue: 'Mar' },
  { flag: 'wednesday', key: 'conseillerTemps.quickAdd.wed', defaultValue: 'Mer' },
  { flag: 'thursday', key: 'conseillerTemps.quickAdd.thu', defaultValue: 'Jeu' },
  { flag: 'friday', key: 'conseillerTemps.quickAdd.fri', defaultValue: 'Ven' },
  { flag: 'saturday', key: 'conseillerTemps.quickAdd.sat', defaultValue: 'Sam' },
  { flag: 'sunday', key: 'conseillerTemps.quickAdd.sun', defaultValue: 'Dim' },
]

const QUICKADD_RECURRENCES = [
  { value: 'weekly', key: 'conseillerTemps.quickAdd.recWeekly', defaultValue: 'Chaque semaine' },
  { value: 'monthly', key: 'conseillerTemps.quickAdd.recMonthly', defaultValue: 'Chaque mois' },
  { value: 'daily', key: 'conseillerTemps.quickAdd.recDaily', defaultValue: 'Tous les jours' },
  { value: 'once', key: 'conseillerTemps.quickAdd.recOnce', defaultValue: 'Ponctuel' },
]

const QUICKADD_DURATIONS = [15, 30, 45, 60, 90, 120, 180, 240]

function quickAddTodayInput() {
  const d = new Date()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

function formatDurationLabel(min) {
  if (min < 60) return `${min} min`
  const h = Math.floor(min / 60)
  const m = min % 60
  return m ? `${h} h ${String(m).padStart(2, '0')}` : `${h} h`
}

// Styles partagés des champs MUI sur fond sombre.
const QUICKADD_FIELD_SX = {
  '& .MuiInputBase-root': {
    color: '#dbe5f7',
    bgcolor: 'rgba(255,255,255,0.04)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.18)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(126,156,219,0.55)',
  },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#7e9cdb',
  },
  '& .MuiInputLabel-root': { color: '#a0b4d6' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#a8c1f0' },
  '& .MuiSvgIcon-root': { color: '#a0b4d6' },
  '& input::-webkit-calendar-picker-indicator': { filter: 'invert(0.8)' },
}

function QuickAddPanel({ onQuickAdd, quickAdding }) {
  const { t } = useTranslation('layout')
  const categoryOptions = getTimeCategoryOptions(t)

  const [title, setTitle] = useState('')
  const [category, setCategory] = useState(categoryOptions[0] || '')
  const [recurrence, setRecurrence] = useState('weekly')
  const [days, setDays] = useState(['monday'])
  const [date, setDate] = useState(quickAddTodayInput())
  const [time, setTime] = useState('09:00')
  const [duration, setDuration] = useState(60)

  const toggleDay = (flag) =>
    setDays((prev) =>
      prev.includes(flag) ? prev.filter((d) => d !== flag) : [...prev, flag],
    )

  const titleOk = title.trim().length > 0
  const daysOk = recurrence !== 'weekly' || days.length > 0
  const canSubmit = titleOk && daysOk && !quickAdding

  const handleSubmit = async () => {
    if (!canSubmit || typeof onQuickAdd !== 'function') return
    const ok = await onQuickAdd({
      title: title.trim(),
      category: category || categoryOptions[0],
      recurrence,
      weekDays: days,
      date,
      startTime: time,
      durationMinutes: Number(duration),
    })
    // On vide le titre après succès, mais on garde les autres réglages pour
    // enchaîner plusieurs ajouts rapides.
    if (ok) setTitle('')
  }

  return (
    <Box
      sx={{
        mb: 2.5,
        p: { xs: 1.5, md: 2 },
        borderRadius: 2,
        bgcolor: 'rgba(126,156,219,0.08)',
        border: '1px solid rgba(126,156,219,0.22)',
      }}
    >
      <Stack gap={1.5}>
        <TextField
          size="small"
          fullWidth
          label={t('conseillerTemps.quickAdd.titleLabel', {
            defaultValue: 'Titre du créneau',
          })}
          placeholder={t('conseillerTemps.quickAdd.titlePlaceholder', {
            defaultValue: 'Ex. Sport, lecture, point projet…',
          })}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={QUICKADD_FIELD_SX}
        />

        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
          gap={1.5}
        >
          <TextField
            select
            size="small"
            fullWidth
            label={t('conseillerTemps.quickAdd.categoryLabel', {
              defaultValue: 'Catégorie',
            })}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            sx={QUICKADD_FIELD_SX}
          >
            {categoryOptions.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            fullWidth
            label={t('conseillerTemps.quickAdd.recurrenceLabel', {
              defaultValue: 'Récurrence',
            })}
            value={recurrence}
            onChange={(e) => setRecurrence(e.target.value)}
            sx={QUICKADD_FIELD_SX}
          >
            {QUICKADD_RECURRENCES.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {t(r.key, { defaultValue: r.defaultValue })}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {recurrence === 'weekly' ? (
          <Box>
            <WTypography
              variant="caption"
              sx={{ color: '#a0b4d6 !important', display: 'block', mb: 0.5 }}
            >
              {t('conseillerTemps.quickAdd.daysLabel', {
                defaultValue: 'Jours de la semaine',
              })}
            </WTypography>
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {QUICKADD_WEEKDAYS.map((d) => {
                const active = days.includes(d.flag)
                return (
                  <Chip
                    key={d.flag}
                    label={t(d.key, { defaultValue: d.defaultValue })}
                    onClick={() => toggleDay(d.flag)}
                    sx={{
                      cursor: 'pointer',
                      fontWeight: 700,
                      bgcolor: active ? '#3b82f6' : 'rgba(255,255,255,0.05)',
                      color: active ? '#fff' : '#a0b4d6',
                      border: active
                        ? '1px solid #3b82f6'
                        : '1px solid rgba(255,255,255,0.15)',
                      '&:hover': {
                        bgcolor: active ? '#2563eb' : 'rgba(255,255,255,0.1)',
                      },
                    }}
                  />
                )
              })}
            </Stack>
          </Box>
        ) : (
          <TextField
            type="date"
            size="small"
            label={t('conseillerTemps.quickAdd.dateLabel', {
              defaultValue: 'Date',
            })}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ ...QUICKADD_FIELD_SX, maxWidth: { sm: 240 } }}
          />
        )}

        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr 1fr', sm: 'auto auto 1fr' }}
          gap={1.5}
          alignItems="center"
        >
          <TextField
            type="time"
            size="small"
            label={t('conseillerTemps.quickAdd.timeLabel', {
              defaultValue: 'Heure',
            })}
            value={time}
            onChange={(e) => setTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ ...QUICKADD_FIELD_SX, minWidth: 130 }}
          />
          <TextField
            select
            size="small"
            label={t('conseillerTemps.quickAdd.durationLabel', {
              defaultValue: 'Durée',
            })}
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            sx={{ ...QUICKADD_FIELD_SX, minWidth: 130 }}
          >
            {QUICKADD_DURATIONS.map((m) => (
              <MenuItem key={m} value={m}>
                {formatDurationLabel(m)}
              </MenuItem>
            ))}
          </TextField>
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              disabled={!canSubmit}
              onClick={handleSubmit}
              startIcon={
                quickAdding ? (
                  <CircularProgress size={14} sx={{ color: '#fff' }} />
                ) : (
                  <EventAvailableIcon />
                )
              }
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 999,
                bgcolor: '#3b82f6',
                '&:hover': { bgcolor: '#2563eb' },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(255,255,255,0.08)',
                  color: 'rgba(219,229,247,0.4)',
                },
              }}
            >
              {quickAdding
                ? t('conseillerTemps.quickAdd.submitting', {
                    defaultValue: 'Ajout…',
                  })
                : t('conseillerTemps.quickAdd.submit', {
                    defaultValue: 'Ajouter à mon agenda',
                  })}
            </Button>
          </Box>
        </Box>
      </Stack>
    </Box>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Time-blocks by horizon
// ─────────────────────────────────────────────────────────────────────────

export function TimeBlocksPanel({
  blocks,
  onSchedule,
  schedulingId,
  onQuickAdd,
  quickAdding,
}) {
  const { t } = useTranslation('layout')
  const [tab, setTab] = useState(0)
  const [quickOpen, setQuickOpen] = useState(false)
  const horizon = TIME_HORIZONS[tab]
  const items = (blocks && blocks[horizon]) || []

  return (
    <Card
      sx={{
        bgcolor: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: { xs: 2, md: 3 },
        color: 'white',
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
        flexWrap="wrap"
        gap={1}
      >
        <WTypography variant="h6" sx={{ fontWeight: 700 }}>
          {t('conseillerTemps.blocks.title', {
            defaultValue: 'Time-blocks à poser dans votre agenda',
          })}
        </WTypography>
        {onQuickAdd && (
          <Button
            size="small"
            onClick={() => setQuickOpen((v) => !v)}
            startIcon={quickOpen ? <CloseIcon /> : <AddIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 999,
              color: '#a8c1f0',
              border: '1px solid rgba(126,156,219,0.45)',
              '&:hover': { bgcolor: 'rgba(126,156,219,0.12)' },
            }}
          >
            {quickOpen
              ? t('conseillerTemps.quickAdd.close', { defaultValue: 'Fermer' })
              : t('conseillerTemps.quickAdd.open', {
                  defaultValue: 'Ajout rapide',
                })}
          </Button>
        )}
      </Box>

      {onQuickAdd && (
        <Collapse in={quickOpen} unmountOnExit>
          <QuickAddPanel onQuickAdd={onQuickAdd} quickAdding={quickAdding} />
        </Collapse>
      )}

      <WTypography
        variant="caption"
        sx={{ color: '#a0b4d6 !important', display: 'block', mb: 1.5 }}
      >
        {t('conseillerTemps.blocks.hint', {
          defaultValue:
            'Sélectionnez un horizon — semaine, mois, trimestre, année.',
        })}
      </WTypography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2,
          '& .MuiTab-root': { color: '#a0b4d6', textTransform: 'none', fontWeight: 600 },
          '& .Mui-selected': { color: '#dbe5f7 !important' },
          '& .MuiTabs-indicator': { backgroundColor: '#7e9cdb' },
        }}
      >
        {TIME_HORIZONS.map((h) => (
          <Tab
            key={h}
            label={t(HORIZON_LABEL_KEY[h], { defaultValue: HORIZON_DEFAULT_LABEL[h] })}
          />
        ))}
      </Tabs>

      {items.length === 0 ? (
        <WTypography variant="body2" sx={{ color: '#a0b4d6 !important', fontStyle: 'italic' }}>
          {t('conseillerTemps.blocks.empty', {
            defaultValue: 'Pas de suggestion pour cet horizon — revenez après avoir enrichi votre profil.',
          })}
        </WTypography>
      ) : (
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
          {items.map((b) => (
            <Box
              key={b.id}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5} flexWrap="wrap" gap={1}>
                <WTypography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {b.title}
                </WTypography>
                <Stack direction="row" gap={0.5}>
                  <Chip
                    size="small"
                    icon={<AccessTimeIcon sx={{ color: '#a0b4d6 !important' }} />}
                    label={b.duration}
                    sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: '#dbe5f7', height: 22 }}
                  />
                  <Chip
                    size="small"
                    label={b.cadence}
                    sx={{
                      bgcolor: 'rgba(126,156,219,0.12)',
                      color: '#a8c1f0',
                      fontWeight: 700,
                      height: 22,
                    }}
                  />
                </Stack>
              </Box>
              <WTypography variant="body2" sx={{ color: '#c1d0eb !important' }}>
                {b.detail}
              </WTypography>
              {/* LAP-765-v2 — CTA "Programmer dans mon agenda".
                  Crée un événement (récurrent si la cadence l'implique) via
                  POST /api/time/events/, et déclenche un re-fetch de la
                  synthèse temps côté parent pour que le bloc programmé soit
                  immédiatement comptabilisé dans la charge hebdo et la
                  répartition par catégorie. */}
              {onSchedule && (
                <Box mt={1.5} display="flex" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={schedulingId === b.id}
                    onClick={() => onSchedule(b, horizon)}
                    startIcon={
                      schedulingId === b.id ? (
                        <CircularProgress size={14} sx={{ color: '#a8c1f0' }} />
                      ) : (
                        <EventAvailableIcon />
                      )
                    }
                    sx={{
                      borderRadius: 999,
                      textTransform: 'none',
                      fontWeight: 700,
                      color: '#a8c1f0',
                      borderColor: 'rgba(126,156,219,0.45)',
                      '&:hover': { bgcolor: 'rgba(126,156,219,0.12)' },
                    }}
                  >
                    {schedulingId === b.id
                      ? t('conseillerTemps.blocks.scheduling', {
                          defaultValue: 'Programmation…',
                        })
                      : t('conseillerTemps.blocks.scheduleCta', {
                          defaultValue: 'Programmer dans mon agenda',
                        })}
                  </Button>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Card>
  )
}
